import { useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { RewardBalanceCardSkeleton } from '@/shared/components/skeletons'
import { captureRedeemScreenViewed } from '@/lib/analytics'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useSessionStore } from '@/stores/useSessionStore'
import { useEkycPromptStore } from '@/stores/useEkycPromptStore'
import {
  clearOnTerminalEkycOutcome,
  isEkycRemindPending,
  isSignupSurveySubmitted,
  setEkycRemindPending,
} from '@/features/signup-survey'
import { EkycUnderReviewBanner } from '@/features/ekyc/components'
import {
  canStartEkycVerification,
  EKYC_UNDER_REVIEW_BLOCK_MESSAGE,
  isEkycUnderReview,
} from '@/features/ekyc/lib/ekycUi'
import { HomeMobilePromo } from '@/features/home/components/HomeMobilePromo'
import { useSpClaimedRewards, useSpProfile, useSpRewardsSummary } from '../api'
import { useNotify } from '@/shared/toast/ToastProvider'
import { ClaimedRewardsList } from '../components/ClaimedRewardsList'
import { RewardsBalanceHighlightCard } from '../components/RewardsBalanceHighlightCard'
import { RewardsGiftCard } from '../components/RewardsGiftCard'
import { RewardsTabBar, type RewardsTab } from '../components/RewardsTabBar'
import { useSpKycConfiguration } from '@/features/kyc-config'
import {
  isRedeemCtaEnabled,
  shouldBlockRedeemForTenantEkyc,
} from '../lib/rewards-redeem-gate'
import {
  rewardsContentInnerSx,
  rewardsContentShellSx,
  rewardsHeroGreetingSx,
  rewardsHeroShellSx,
  rewardsHeroSx,
  rewardsHeroWelcomeSx,
  rewardsPageRootSx,
  rewardsSubtitleSx,
  rewardsTitleSx,
  rewardsClaimedWrapSx,
} from '../styles/rewardsPageStyles'

function parseTab(value: string | null): RewardsTab {
  return value === 'claimed' ? 'claimed' : 'available'
}

function greetingFirstName(displayName: string | undefined): string {
  const part = displayName?.trim().split(/\s+/)[0]
  return part || 'there'
}

export function RewardsPage() {
  useDocumentTitle('Rewards')
  const navigate = useNavigate()
  const { transactionId: deepLinkTransactionId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const userName = useSessionStore((s) => s.user?.name)
  const firstName = greetingFirstName(userName)

  const activeTab = parseTab(searchParams.get('tab') ?? (deepLinkTransactionId ? 'claimed' : null))

  // Sync ?tab=claimed when landing from email deep link only (not when user switches tabs).
  useEffect(() => {
    if (!deepLinkTransactionId) return
    setSearchParams({ tab: 'claimed' }, { replace: true })
  }, [deepLinkTransactionId, setSearchParams])

  const { data: summary, isLoading: summaryLoading } = useSpRewardsSummary()
  const { data: claimedPage, isLoading: claimedLoading } = useSpClaimedRewards()
  const { data: profile } = useSpProfile()
  const { data: tenantKycMode } = useSpKycConfiguration()
  const { showToast } = useNotify()

  const balance = summary?.availableBalanceUsd ?? 0
  const redeemEnabled = isRedeemCtaEnabled({
    availableBalanceUsd: balance,
    redeemFulfillmentAvailable: summary?.redeemFulfillmentAvailable ?? true,
    profile,
    tenantKycMode,
  })
  const underReview = isEkycUnderReview(profile?.ekycStatus)

  const userId = useSessionStore((s) => s.user?.id)
  const openFromChip = useEkycPromptStore((s) => s.openFromChip)
  const canStartEkyc = canStartEkycVerification(profile)
  const skipSignupKycPrompt = searchParams.get('from') === 'signup-survey'
  useEffect(() => {
    captureRedeemScreenViewed(skipSignupKycPrompt ? 'signup_survey' : 'direct')
  }, [skipSignupKycPrompt])

  const signupSubmitted = Boolean(userId && isSignupSurveySubmitted(userId))
  const remindPending = Boolean(userId && isEkycRemindPending(userId))
  const ekycVerified = profile?.ekycStatus === 'verified'
  const terminalEkycFail =
    profile?.ekycStatus === 'failed' &&
    profile?.ekycAttemptUsed === true &&
    profile?.ekycFailureReason !== 'abandoned' &&
    profile?.ekycFailureReason !== 'expired'

  useEffect(() => {
    // Remind Me Later OR signup submitted without eKYC yet (Claim Now / direct Rewards).
    // BFP-1131 — skip the auto KYC popup only on this post-account landing.
    if (skipSignupKycPrompt) return
    if (!userId || (!remindPending && !signupSubmitted)) return
    if (terminalEkycFail) {
      clearOnTerminalEkycOutcome(userId)
      return
    }
    if (ekycVerified) {
      setEkycRemindPending(userId, false)
      return
    }
    if (underReview) return
    if (!canStartEkyc) return
    openFromChip()
  }, [
    userId,
    remindPending,
    signupSubmitted,
    terminalEkycFail,
    ekycVerified,
    underReview,
    canStartEkyc,
    openFromChip,
    skipSignupKycPrompt,
  ])

  const handleTabChange = (tab: RewardsTab) => {
    if (tab === 'available') {
      navigate('/rewards', { replace: true })
      return
    }
    if (deepLinkTransactionId) {
      navigate(`/rewards/claimed/${deepLinkTransactionId}?tab=claimed`, { replace: true })
      return
    }
    navigate('/rewards?tab=claimed', { replace: true })
  }

  const handleRedeem = () => {
    if (underReview) {
      showToast(EKYC_UNDER_REVIEW_BLOCK_MESSAGE, 'error')
      return
    }
    if (tenantKycMode === 'unknown') {
      showToast('Rewards are temporarily unavailable. Please try again later.', 'error')
      return
    }
    if (shouldBlockRedeemForTenantEkyc(tenantKycMode, profile)) {
      if (terminalEkycFail) {
        showToast('Identity verification is unavailable. Contact support if you need help.', 'error')
        return
      }
      if (canStartEkyc) {
        openFromChip()
        return
      }
      showToast('Complete identity verification to redeem rewards.', 'error')
      return
    }
    navigate('/rewards/select-brand')
  }

  return (
    <Box sx={rewardsPageRootSx} data-testid="provider-rewards-page">
      <Box sx={rewardsHeroSx}>
        <Box sx={rewardsHeroShellSx}>
          <Box sx={rewardsContentInnerSx}>
            <Typography component="h1" sx={rewardsHeroGreetingSx}>
              {`Hey, ${firstName}!`}
            </Typography>
            <Typography sx={rewardsHeroWelcomeSx}>Welcome to Brushfire</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={rewardsContentShellSx}>
        <Box sx={rewardsContentInnerSx}>
          <Typography component="h2" sx={rewardsTitleSx}>
            Rewards
          </Typography>
          <Typography sx={rewardsSubtitleSx}>
            View your reward balance and redeem available rewards
          </Typography>

          <EkycUnderReviewBanner />

          {activeTab === 'available' ? (
            <>
              {summaryLoading ? (
                <RewardBalanceCardSkeleton testId="rewards-summary-loading" />
              ) : (
                <RewardsBalanceHighlightCard balanceUsd={balance} />
              )}

              <RewardsTabBar activeTab={activeTab} onTabChange={handleTabChange} />

              {summaryLoading ? null : (
                <RewardsGiftCard redeemEnabled={redeemEnabled} onRedeem={handleRedeem} />
              )}
            </>
          ) : (
            <>
              {summaryLoading ? (
                <RewardBalanceCardSkeleton testId="rewards-summary-loading" />
              ) : (
                <RewardsBalanceHighlightCard balanceUsd={balance} />
              )}

              <RewardsTabBar activeTab={activeTab} onTabChange={handleTabChange} />

              <Box sx={rewardsClaimedWrapSx}>
                <ClaimedRewardsList
                  rewards={claimedPage?.items ?? []}
                  isLoading={claimedLoading}
                  openTransactionId={deepLinkTransactionId}
                />
              </Box>
            </>
          )}

          <HomeMobilePromo />
        </Box>
      </Box>
    </Box>
  )
}
